import { WindowTitle } from '../../../core/window-title/window-title.component';
import { useTeamMemberDetails } from './hooks/team-member-details.hook';
import { useTeamMemberEntityHistory } from './hooks/use-team-member-entity-history.hook';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { TeamMemberDetailsActionsObserver } from './team-member-details-actions-observer.component';
import { ProjectPathParams, Paths } from '../../../app/router/app.router';
import { RestError } from '@azure/core-http';
import { LoadingIndicator } from '../../../ui/components/pure/loading-indicator/loading-indicator.component';
import { makeTeamMemberDetailsHeaderProps } from './logic/team-member-details-props.map';
import { TeamMemberDetailsPageHeader } from '../../../ui/components/pure/page-headers/details/team-member/team-member-details-page-header.component';
import { EntityHistoryTimelineCard } from '../../../ui/components/pure/entity-history-timeline/entity-history-timeline-card/entity-history-timeline-card.component';
import { useQueryGetAccountTeamMemberships } from '../accounts.queries';
import { useAccessLevelDescriptions } from '../hooks/use-access-level-descriptions.hook';
import { Permissions } from '../../../core/permissions/permissions.typings';

export function TeamMemberDetailsPage({ params }: { params: ProjectPathParams<Paths.teamMember> }) {
  const [user, actions] = useTeamMemberDetails(params.projectID, params.teamMemberID);
  const entityHistory = useTeamMemberEntityHistory(user.data?.accountID);
  const memberships = useQueryGetAccountTeamMemberships(params.teamMemberID);
  const accessLevelDescriptions = useAccessLevelDescriptions(params.projectID);

  const error = user.error;

  if (error) {
    if ((error as RestError).statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" entity="customer" />;
    }

    if ((error as RestError).statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" entity="customer" />;
    }

    return <ErrorFullPage />;
  }

  if (user.isLoading) {
    return <LoadingIndicator fullPage />;
  }

  if (!user.data) {
    return <ErrorFullPage />;
  }

  const headerProps = makeTeamMemberDetailsHeaderProps(
    params.projectID,
    user.data,
    actions.setAction,
    accessLevelDescriptions,
    memberships.data?.items
  );

  return (
    <>
      <WindowTitle detail={`${user.data.firstName} ${user.data.lastName}`} pageTitle="Team Members" />
      <TeamMemberDetailsPageHeader {...headerProps} />
      <TeamMemberDetailsActionsObserver params={params} user={user.data} actions={actions} />
      <EntityHistoryTimelineCard
        {...entityHistory}
        currentProjectID={params.projectID}
        permission={Permissions.accounts.get}
      />
    </>
  );
}
